import React, { useRef, useEffect, useState } from "react";
import "./Home.css";
import { motion } from "framer-motion";
import { BrotherhoodImage66, BrotherhoodImage76 } from "../Assets";
import { knightAKYLogo } from "../Assets";
import SleekButton from "../Components/SleekButton";
import { OpenInNew } from "@mui/icons-material";
import { Instagram, Facebook, LinkedIn } from "@mui/icons-material";
import { BrotherhoodImage75 } from "../Assets";
import HomeHero from "../Components/HomeHero";

/* Which hero state the site is in. Switching this single value moves the whole
   hero between the teaser countdown, the rush-week video, and the plain title —
   all three are already built and styled in HomeHero.jsx.

   'video' -> 'default' once rush is over — the title alone, no video
   request, no timer. */
const HERO_VARIANT = "video";

export default function Home() {
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);
  const infoSectionRef = useRef(null);

  // Animation variants
  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  const scaleInVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 5000);

    const revealPage = () => {
      clearTimeout(timer);
      setIsLoading(false);
    };

    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.addEventListener("canplay", revealPage);
      // If the video 404s or the CDN is unreachable, "canplay" never fires and
      // the page sat behind the loader for the full 5s. Reveal immediately
      // instead — the hero just renders without its video.
      videoElement.addEventListener("error", revealPage);
      // A src that fails to resolve at all surfaces here rather than as "error".
      if (videoElement.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
        revealPage();
      }
    } else {
      revealPage();
    }

    return () => {
      clearTimeout(timer);
      if (videoElement) {
        videoElement.removeEventListener("canplay", revealPage);
        videoElement.removeEventListener("error", revealPage);
      }
    };
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const animateOnScroll = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = "running";
        }
      });
    };

    const observer = new IntersectionObserver(animateOnScroll, observerOptions);

    const sections = document.querySelectorAll(
      ".info-content, .info-image-container, .info-button-container, .wrapup-section, .summary-item, .final-statements, .social-links",
    );
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <>
      {isLoading && (
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      )}
      <div className={`home-container ${isLoading ? "hidden" : ""}`}>
        <HomeHero
          variant={HERO_VARIANT}
          videoRef={videoRef}
          onTitleClick={() =>
            infoSectionRef.current?.scrollIntoView({ behavior: "smooth" })
          }
        />
        <div
          className="section-background info-background"
          ref={infoSectionRef}
        >
          <div className="section-overlay"></div>
          <div className="info-section">
            <h2 className="section-title">
              THE FIRST AND LARGEST BUSINESS FRATERNITY
            </h2>
            <motion.div
              className="info-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
                },
              }}
            >
              <p>
                The organization of Alpha Kappa Psi was founded in New York
                University in 1904. Spanning decades, Alpha Kappa Psi has helped
                over 300,000 individuals create lifelong friends and pursue
                their dreams. Our goal is to help Anteaters become the best
                versions of themselves.
              </p>
            </motion.div>
            <motion.div
              className="info-image-container"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{
                hidden: { opacity: 0, scale: 0.9 },
                visible: {
                  opacity: 1,
                  scale: 1,
                  transition: {
                    duration: 0.5,
                    delay: 0.1,
                    ease: [0.16, 1, 0.3, 1],
                  },
                },
              }}
            >
              <img
                className="info-image"
                src={knightAKYLogo}
                alt="Knight Logo of Alpha Kappa Psi"
              />
              <div className="image-glow"></div>
            </motion.div>
            <motion.div
              className="info-button-container"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    delay: 0.2,
                    ease: [0.16, 1, 0.3, 1],
                  },
                },
              }}
            >
              <SleekButton
                variant="outlined"
                href="https://akpsi.org/"
                target="_blank"
                endIcon={<OpenInNew />}
                className="sleekButton"
              >
                Learn More
              </SleekButton>
            </motion.div>
          </div>
        </div>
        <div className="pillar-background">
          <div className="pillar-overlay"></div>
          <div className="wrapup-section">
            <h2 className="pillar-title">OUR PILLARS</h2>
            <div className="summary-section">
              <motion.div
                className="summary-item brotherhood"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={scaleInVariants}
                custom={0}
              >
                <div className="item-content">
                  <h2>BROTHERHOOD</h2>
                  <div className="image-container">
                    <img src={BrotherhoodImage76} alt="Brotherhood" />
                    <div className="image-overlay"></div>
                  </div>
                  <p>
                    In Alpha Kappa Psi, you meet as strangers but leave as
                    lifelong friends. Our brotherhood sets us apart and shapes
                    us into who we are.
                  </p>
                  <SleekButton className="sleekButton" href="/meet-us">
                    Brothers
                  </SleekButton>
                </div>
              </motion.div>

              <motion.div
                className="summary-item professionalism"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={scaleInVariants}
                custom={1}
                transition={{ delay: 0.2 }}
              >
                <div className="item-content">
                  <h2>PROFESSIONALISM</h2>
                  <div className="image-container">
                    <img src={BrotherhoodImage66} alt="Professionalism" />
                    <div className="image-overlay"></div>
                  </div>
                  <p>
                    We inspire one another to chase our passions with confidence
                    by equipping ourselves with the tools necessary to succeed
                    in any industry.
                  </p>
                  <SleekButton className="sleekButton" href="/careers">
                    Careers
                  </SleekButton>
                </div>
              </motion.div>

              <motion.div
                className="summary-item personal-growth"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={scaleInVariants}
                custom={2}
                transition={{ delay: 0.4 }}
              >
                <div className="item-content">
                  <h2>PERSONAL GROWTH</h2>
                  <div className="image-container">
                    <img src={BrotherhoodImage75} alt="Personal Growth" />
                    <div className="image-overlay"></div>
                  </div>
                  <p>
                    Our unique culture inspires, encourages, and motivates you
                    to step out of your comfort zone and live the life you've
                    always imagined.
                  </p>
                  {/* Was href="/recruitment", which is not a route — App.jsx
                      registers this page at /rush, so the button 404'd. */}
                  <SleekButton className="sleekButton" href="/rush">
                    Recruitment
                  </SleekButton>
                </div>
              </motion.div>
            </div>
            <footer className="site-footer">
              <div className="footer-content">
                <motion.div
                  className="social-links"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInVariants}
                >
                  <a
                    href="https://www.instagram.com/akpsiuci"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                  >
                    <Instagram />
                  </a>
                  <a
                    href="https://facebook.com/akpsiuci"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                  >
                    <Facebook />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/alpha-kappa-psi-uci"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                  >
                    <LinkedIn />
                  </a>
                  <a
                    href="mailto:akpsi.uci.rush@gmail.com"
                    className="email-link"
                  >
                    akpsi.uci.rush@gmail.com
                  </a>
                </motion.div>
                <motion.div
                  className="final-statements"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInVariants}
                >
                  <p className="statement-one">
                    Our members strive to uphold the highest standards in
                    everything they do.
                  </p>
                  <p className="statement-two">
                    Site designed and developed in-house, on 7000 lines of code
                    and counting...
                  </p>
                </motion.div>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
}
